import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common/FormField";
import { SelectField } from "@/components/common/SelectField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AppDialog from "@/components/common/AppDialog";
import { useTran } from "@/hooks";
import { showToast } from "@/lib/utils/toast";
import { customDisclosureStore } from "../../stores/custom-disclosure.store";

const INPUT_TYPES = [
  { value: "simple", label: "Simple text" },
  { value: "simpleLongText", label: "Long text" },
  { value: "value", label: "Numeric value" },
  { value: "valueUnit", label: "Value with unit" },
  { value: "valueUnitDenominator", label: "Value with unit and denominator" },
  { value: "yearValue", label: "Year + Value" },
  { value: "yearValueUnit", label: "Year + Value + Unit" },
  { value: "yearValueCategory", label: "Year + Value + Category" },
  { value: "yearLocationValueUnit", label: "Year + Location + Value + Unit" },
];

const CATEGORIES = [
  { value: "Environment", label: "Environment", color: "blue" },
  { value: "Social", label: "Social", color: "purple" },
  { value: "Governance", label: "Governance", color: "amber" },
];

const FREQUENCIES = [
  { value: "annual", label: "Annual" },
  { value: "quarterly", label: "Quarterly" },
  { value: "monthly", label: "Monthly" },
];

const CustomDisclosureDialog = observer(
  ({ open, onOpenChange, editingDisclosure }) => {
    const { t } = useTran();

    const [formData, setFormData] = useState({
      code: "",
      title: "",
      category: "Environment",
      inputType: "simple",
      description: "",
      unitOptions: "",
      defaultUnit: "",
      frequency: "",
      isQuantitative: false,
      tags: "",
    });

    const [loading, setLoading] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
      if (editingDisclosure) {
        setFormData({
          code: editingDisclosure.code || "",
          title: editingDisclosure.title || "",
          category: editingDisclosure.category || "Environment",
          inputType: editingDisclosure.inputType || "simple",
          description: editingDisclosure.description || "",
          unitOptions: editingDisclosure.unitOptions?.join(", ") || "",
          defaultUnit: editingDisclosure.defaultUnit || "",
          frequency: editingDisclosure.frequency || "",
          isQuantitative: editingDisclosure.isQuantitative || false,
          tags: editingDisclosure.tags?.join(", ") || "",
        });
        setShowAdvanced(true);
      } else {
        setFormData({
          code: "",
          title: "",
          category: "Environment",
          inputType: "simple",
          description: "",
          unitOptions: "",
          defaultUnit: "",
          frequency: "",
          isQuantitative: false,
          tags: "",
        });
        setShowAdvanced(false);
      }
    }, [editingDisclosure, open]);

    const handleChange = (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
      // Validation
      if (!formData.code.trim()) {
        showToast.error(t("Code is required"));
        return;
      }
      if (!formData.title.trim()) {
        showToast.error(t("Title is required"));
        return;
      }
      if (!/^[A-Z0-9-_]+$/i.test(formData.code)) {
        showToast.error(
          t("Code can only contain letters, numbers, hyphens, and underscores"),
        );
        return;
      }

      setLoading(true);
      try {
        const disclosure = {
          code: formData.code.trim().toUpperCase(),
          title: formData.title.trim(),
          category: formData.category,
          inputType: formData.inputType,
          description: formData.description.trim(),
          unitOptions: formData.unitOptions
            ? formData.unitOptions
                .split(",")
                .map((u) => u.trim())
                .filter(Boolean)
            : [],
          defaultUnit: formData.defaultUnit.trim() || null,
          frequency:
            formData.frequency === "none" ? null : formData.frequency || null,
          isQuantitative: formData.isQuantitative,
          tags: formData.tags
            ? formData.tags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean)
            : [],
        };

        if (editingDisclosure) {
          await customDisclosureStore.updateDisclosure(
            editingDisclosure._id,
            disclosure,
          );
          showToast.success(t("Custom disclosure updated successfully"));
        } else {
          await customDisclosureStore.createDisclosure(disclosure);
          showToast.success(t("Custom disclosure created successfully"));
        }

        onOpenChange(false);
      } catch (error) {
        showToast.error(
          error?.message || t("Failed to save custom disclosure"),
        );
      } finally {
        setLoading(false);
      }
    };

    return (
      <AppDialog
        open={open}
        onOpenChange={onOpenChange}
        size="lg"
        header={
          <p className="text-xl font-semibold">
            {editingDisclosure
              ? t("Edit Custom Disclosure")
              : t("Create Custom Disclosure")}
          </p>
        }
      >
        {/* Code */}
        <FormField
          label={t("Code")}
          required
          id="code"
          value={formData.code}
          onChange={(e) => handleChange("code", e.target.value)}
          placeholder="e.g., ORG-CUSTOM-001"
          disabled={!!editingDisclosure}
        />
        <p className="text-xs text-muted-foreground -mt-2">
          {t("Unique identifier (letters, numbers, hyphens, underscores)")}
        </p>

        {/* Title */}
        <FormField
          label={t("Title")}
          required
          id="title"
          value={formData.title}
          onChange={(e) => handleChange("title", e.target.value)}
          placeholder={t("e.g., Employee Mental Health Days")}
        />

        {/* Category */}
        <div>
          <Label htmlFor="category">{t("Category")} *</Label>
          <div className="mt-2 flex gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                type="button"
                className={`flex-1 rounded-radius border px-4 py-2 text-sm font-medium transition-colors ${
                  formData.category === cat.value
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background hover:bg-muted"
                }`}
                onClick={() => handleChange("category", cat.value)}
              >
                {t(cat.label)}
              </button>
            ))}
          </div>
        </div>

        {/* Input Type */}
        <SelectField
          label={t("Input Type")}
          required
          value={formData.inputType}
          onValueChange={(v) => handleChange("inputType", v)}
          options={INPUT_TYPES.map((type) => ({
            key: type.value,
            label: t(type.label),
          }))}
          placeholder={t("Select input type...")}
        />

        {/* Description */}
        <div>
          <Label htmlFor="description">
            {t("Description")} ({t("optional")})
          </Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            placeholder={t("Provide guidance for data entry...")}
            className="mt-1"
            rows={3}
          />
        </div>

        {/* Advanced Options */}
        <div>
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? "▼" : "▶"} {t("Advanced options")}
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4 rounded-radius border border-border bg-muted/30 p-4">
              {/* Unit Options */}
              <FormField
                label={t("Unit Options")}
                id="unitOptions"
                value={formData.unitOptions}
                onChange={(e) => handleChange("unitOptions", e.target.value)}
                placeholder={t("e.g., kg, tons, liters (comma-separated)")}
              />
              <p className="text-xs text-muted-foreground -mt-2">
                {t("Comma-separated list of allowed units")}
              </p>

              {/* Default Unit */}
              <FormField
                label={t("Default Unit")}
                id="defaultUnit"
                value={formData.defaultUnit}
                onChange={(e) => handleChange("defaultUnit", e.target.value)}
                placeholder={t("e.g., kg")}
              />

              {/* Frequency */}
              <SelectField
                label={t("Reporting Frequency")}
                value={formData.frequency || "none"}
                onValueChange={(v) => handleChange("frequency", v)}
                options={[
                  { key: "none", label: t("None") },
                  ...FREQUENCIES.map((freq) => ({
                    key: freq.value,
                    label: t(freq.label),
                  })),
                ]}
                placeholder={t("Select frequency...")}
              />

              {/* Tags */}
              <FormField
                label={t("Tags")}
                id="tags"
                value={formData.tags}
                onChange={(e) => handleChange("tags", e.target.value)}
                placeholder={t("e.g., DEI, Innovation (comma-separated)")}
              />

              {/* Is Quantitative */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isQuantitative"
                  checked={formData.isQuantitative}
                  onChange={(e) =>
                    handleChange("isQuantitative", e.target.checked)
                  }
                  className="h-4 w-4"
                />
                <Label htmlFor="isQuantitative" className="cursor-pointer">
                  {t("This is a quantitative metric")}
                </Label>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {t("Cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading
              ? t("Saving...")
              : editingDisclosure
                ? t("Update")
                : t("Create")}
          </Button>
        </div>
      </AppDialog>
    );
  },
);

export default CustomDisclosureDialog;
